import { useState, useMemo } from 'react'
import { useEconomyStore } from '@/state/useEconomyStore'
import { generateVillagers } from '@/economy/villagers'
import { directBuy, getMarketPrices } from '@/economy/api'
import { ITEM_DISPLAY_NAMES, SPECIALTY_DISPLAY, formatFollis } from '@/economy/types'
import type { WalletInfo, ItemType } from '@/economy/types'
import type { InteractionTarget } from '@/state/useEconomyStore'

interface PanelProps { interaction: InteractionTarget; wallet: WalletInfo | null }

const FARM_DISCOUNT = 0.9  // 10% cheaper than market
const FARM_ITEMS: Array<'wheat' | 'fruit'> = ['wheat', 'fruit']

export function FarmPanel({ interaction, wallet }: PanelProps) {
  const [selectedItem, setSelectedItem] = useState<ItemType | ''>('')
  const [quantity, setQuantity] = useState(5)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const villageData = useEconomyStore((s) => s.villageData.get(interaction.villageIndex))
  const updateCargoFromTrade = useEconomyStore((s) => s.updateCargoFromTrade)
  const setMarketPrices = useEconomyStore((s) => s.setMarketPrices)

  const prices = villageData?.marketPrices ?? []
  const priceMap = new Map(prices.map((p) => [p.item, p]))

  const farmer = useMemo(() => {
    if (!interaction.profileType) return null
    const seed = 12345 + interaction.villageIndex * 777
    const sizes: Array<'small' | 'medium' | 'large'> = ['small', 'medium', 'large']
    const size = sizes[interaction.villageIndex % 3]
    const houseCounts = { small: 8, medium: 14, large: 22 }
    const villagers = generateVillagers(seed, size, interaction.profileType, houseCounts[size], prices.length > 0 ? prices : null)
    return villagers.find((v) => v.specialty === 'farmer') || villagers[0]
  }, [interaction.villageIndex, interaction.profileType, prices])

  const selectedPrice = selectedItem ? priceMap.get(selectedItem) : null
  const estimatedCost = selectedPrice ? Math.ceil(selectedPrice.spot_price * quantity * FARM_DISCOUNT) : 0

  async function handleBuy() {
    if (!selectedItem || interaction.serverId === null) return
    setLoading(true)
    setError(null)
    try {
      const result = await directBuy(interaction.serverId, selectedItem, quantity, FARM_DISCOUNT)
      updateCargoFromTrade(selectedItem, result.new_item_qty, result.new_follis_balance)
      const newPrices = await getMarketPrices(interaction.serverId)
      setMarketPrices(interaction.villageIndex, newPrices)
    } catch (e: any) {
      setError(e.message || 'Purchase failed')
    }
    setLoading(false)
  }

  return (
    <>
      {farmer && (
        <div className="panel-section">
          <h3 className="panel-section__title">{farmer.name} — {SPECIALTY_DISPLAY[farmer.specialty]}</h3>
          <p style={{ color: '#8b7355', fontFamily: 'Crimson Text, serif', fontSize: 13 }}>
            "Fresh from the fields! Better prices than the market."
          </p>
        </div>
      )}

      <div className="panel-section">
        <h3 className="panel-section__title">Farm Stand (10% off market)</h3>
        {error && <div className="panel-error">{error}</div>}
        {interaction.serverId === null && <p className="panel-placeholder">No active market.</p>}
        {FARM_ITEMS.map((item) => {
          const price = priceMap.get(item)
          const discountPrice = price ? price.spot_price * FARM_DISCOUNT : null
          return (
            <div key={item} className="panel-row" style={{ cursor: 'pointer' }} onClick={() => setSelectedItem(item)}>
              <span className="panel-row__label" style={{ fontWeight: selectedItem === item ? 700 : 400 }}>
                {ITEM_DISPLAY_NAMES[item]}
              </span>
              <span className="panel-row__value">
                {discountPrice != null ? `${discountPrice.toFixed(1)}ƒ/ea` : 'No market data'}
                {price && (
                  <span style={{ color: '#8b7355', fontSize: 11, marginLeft: 6, textDecoration: 'line-through' }}>
                    {price.spot_price.toFixed(1)}ƒ
                  </span>
                )}
              </span>
            </div>
          )
        })}
      </div>

      {selectedItem && (
        <div className="panel-section">
          <h3 className="panel-section__title">Purchase</h3>
          <div className="panel-input-row">
            <span style={{ color: '#c4b99a', fontFamily: 'Crimson Text, serif', fontSize: 14 }}>
              {ITEM_DISPLAY_NAMES[selectedItem]}
            </span>
            <span style={{ color: '#8b7355', fontSize: 12 }}>Qty:</span>
            <input
              type="number"
              className="panel-number-input"
              value={quantity}
              min={1}
              max={100}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            />
          </div>
          <div className="panel-cost">&asymp; {formatFollis(estimatedCost)}</div>
          <button
            className="panel-btn panel-btn--primary"
            style={{ width: '100%' }}
            disabled={loading || !wallet || wallet.follis < estimatedCost}
            onClick={handleBuy}
          >
            {loading ? 'Buying...' : `Buy ${quantity}× ${ITEM_DISPLAY_NAMES[selectedItem]}`}
          </button>
        </div>
      )}
    </>
  )
}
